import type {
    SelectedResumeType,
    JobSeekerProfileLatestAppliedResumeType,
} from '../../__generated__/graphql-base-types';

/**
 * Latest-apply values that have no setup-selectable equivalent and intentionally
 * yield no pre-selection: the codegen future-proofing member plus absent values.
 * Used by the exhaustiveness guard below so that if codegen ever adds a new
 * *concrete* JobSeekerProfileLatestAppliedResumeType member, the `default` branch
 * stops compiling and forces an explicit mapping decision.
 */
type UnsupportedLatestApplyResumeType = '%future added value' | null | undefined;

const assertUnsupportedLatestApplyResumeType = (value: UnsupportedLatestApplyResumeType): void => {
    // Compile-time only: the value is intentionally unused at runtime.
    void value;
};

/**
 * Map the user's most-recent-apply resume type to a setup-selectable resume
 * type (IAFE-2079).
 *
 * The setup resume picker only supports Indeed Resume and saved file resumes.
 * Per product, a TAILORED_RESUME latest-apply falls back to the Indeed Resume
 * (tailored resumes are derived from the Indeed profile), while a
 * TAILORED_RESUME_FILE falls back to the saved file resume. An unset or unknown
 * value yields `undefined` — no pre-selection.
 *
 * Used as a fallback source only when there is no explicit `selectedResumeType`
 * and only under the progressive-automation proctor bucket.
 */
export const mapLatestApplyResumeType = (
    latest: JobSeekerProfileLatestAppliedResumeType | null | undefined,
): SelectedResumeType | undefined => {
    switch (latest) {
        case 'INDEED_RESUME':
        case 'TAILORED_RESUME':
            return 'INDEED_RESUME';
        case 'SAVED_FILE_RESUME':
        case 'TAILORED_RESUME_FILE':
            return 'SAVED_FILE_RESUME';
        default:
            // Only '%future added value', null, and undefined should reach here.
            // A newly generated concrete enum member fails this call at compile
            // time, prompting an explicit mapping decision above.
            assertUnsupportedLatestApplyResumeType(latest);
            return undefined;
    }
};
